import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { deleteApiListingsById, getApiListingsMyListings, Listing, Status } from '../../api/listings-client';
import Footer from '../Footer';
import MainHeader from '../MainHeader';
import { capitalizeFirstLetters } from '../../util/AddressUtil';
import { formatAvailableDate } from '../../util/DateFormatterUtil';
import { MapPin, Edit, Trash2, Calendar, Clock } from 'lucide-react';
import LazyImage from '../../components/LazyImage';
import { PageContainer } from './LandingPage';
import DeleteConfirmationModal from './ManageListings/DeleteConfirmationModal';

const ManageListings = () => {
    const [listings, setListings] = useState<Listing[]>([]);
    const [deletingListings, setDeletingListings] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // New state for delete confirmation modal
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [listingToDelete, setListingToDelete] = useState<Listing | null>(null);

    useEffect(() => {
        const fetchListings = async () => {
            setIsLoading(true);
            try {
                const response = await getApiListingsMyListings();
                setListings(response);
            } catch (error) {
                console.error('Error fetching listings:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchListings();
    }, []);

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        });
    };

    const getStatusBadge = (status: Status | undefined) => {
        if (!status) return null;
        switch (status) {
            case Status.REVIEW:
                return (
                    <span className="inline-flex items-center gap-1.5 py-0.5 px-2 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Under Review
                    </span>
                );
            case Status.APPROVED:
                return (
                    <span className="inline-flex items-center gap-1.5 py-0.5 px-2 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                        Live
                    </span>
                );
            case Status.REJECTED:
                return (
                    <span className="inline-flex items-center gap-1.5 py-0.5 px-2 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Rejected
                    </span>
                );
            case Status.PAUSED:
                return (
                    <span className="inline-flex items-center gap-1.5 py-0.5 px-2 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Paused
                    </span>
                );
            default:
                return null;
        }
    };

    // Updated handleDelete to open confirmation modal
    const handleDelete = (listing: Listing, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setListingToDelete(listing);
        setIsDeleteModalOpen(true);
    };

    // New function to handle the actual deletion after confirmation
    const confirmDelete = async () => {
        if (!listingToDelete || !listingToDelete.id) return;

        const id = listingToDelete.id;
        setDeletingListings(prev => new Set(prev).add(id));

        try {
            await deleteApiListingsById({ id });
            setListings(prevListings => prevListings.filter(listing => listing.id !== id));
        } catch (error) {
            console.error('Error deleting listing:', error);
            // Optionally, show an error message to the user
        } finally {
            setDeletingListings(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });

            // Close the modal after deletion
            closeDeleteModal();
        }
    };

    // Function to close the delete modal
    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setListingToDelete(null);
    };

    // Admin version of ListingCard specifically for ManageListings
    const AdminListingCard = ({ listing }: { listing: Listing }) => {
        const today = new Date().toISOString().split('T')[0];
        const availabilityText = listing && listing?.availableFrom
            ? (new Date(listing.availableFrom) <= new Date(today)
                ? 'Available now'
                : `Available ${formatAvailableDate(listing.availableFrom)}`)
            : '';

        // Get the image URL if available
        const imageUrl = listing && listing?.images?.at(0)?.small ? listing.images.at(0).small : '';
        const isDeleting = deletingListings.has(listing.id ?? '');
        const isRejected = listing.status === Status.REJECTED;

        return (
            <div className="group relative w-full flex flex-col rounded-xl bg-white border border-gray-200 h-full shadow-md transition-all duration-200 ease-in-out hover:shadow-lg">
                {/* Gradient border animation */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#24ccf2]/0 via-[#2476f2]/0 to-purple-500/0 opacity-0 group-hover:opacity-100 group-hover:from-[#24ccf2]/10 group-hover:via-[#2476f2]/10 group-hover:to-purple-500/10 transition-opacity duration-200 ease-in-out -m-[1px]" />

                {/* Status badge - positioned absolutely at top left */}
                <div className="absolute top-2 left-2 z-20">
                    {getStatusBadge(listing.status)}
                </div>

                {/* Edit button - positioned absolutely at top right */}
                <Link
                    to={`/listing/${listing.id}`}
                    className={`bg-white p-2 rounded-full shadow-md absolute top-2 right-2 flex justify-center items-center w-10 h-10 z-20 text-[#2476f2] hover:text-blue-700 transition-colors ${isRejected ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={(e) => {
                        // Prevent navigation if the button is disabled
                        if (isRejected || isDeleting) {
                            e.preventDefault();
                        }
                    }}
                    aria-label="Edit listing"
                >
                    <Edit className="w-5 h-5" />
                </Link>

                {/* Delete button - positioned absolutely at top right, but offset */}
                <button
                    className="bg-white p-2 rounded-full shadow-md absolute top-2 right-14 flex justify-center items-center w-10 h-10 z-20 text-red-600 hover:text-red-700 transition-colors"
                    onClick={(e) => handleDelete(listing, e)}
                    disabled={isDeleting}
                    aria-label="Delete listing"
                >
                    {isDeleting ? (
                        <span className="animate-spin inline-block w-5 h-5 border-2 border-current border-t-transparent text-[#2476f2] rounded-full" role="status" aria-label="loading"></span>
                    ) : (
                        <Trash2 className="w-5 h-5" />
                    )}
                </button>

                {/* The actual content */}
                <div className="flex flex-col h-full text-left relative">
                    {/* Image Container */}
                    <div className="relative pb-[70%] w-full overflow-hidden rounded-t-xl">
                        <div className="absolute inset-0">
                            <LazyImage
                                src={imageUrl}
                                alt={listing?.suburb ? capitalizeFirstLetters(listing.suburb) : 'Listing image'}
                                className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-200 ease-in-out group-hover:scale-105"
                            />
                        </div>
                    </div>

                    {/* Content area */}
                    <div className="p-4 flex flex-col gap-2">
                        {/* Price */}
                        <div className="flex items-baseline gap-1">
                            <span className="text-lg font-medium text-gray-900">${listing?.rent} / week</span>
                        </div>

                        {/* Location */}
                        <div className="flex items-center gap-1.5 text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <p className="text-sm">
                                {capitalizeFirstLetters(listing?.suburb || '')}, {listing?.state} {listing?.postcode}
                            </p>
                        </div>

                        {/* Availability */}
                        <div className="inline-flex">
                            <span className={`text-sm px-2.5 py-1 rounded-full ${availabilityText === 'Available now'
                                ? 'bg-[#24ccf2]/10 text-[#2476f2]'
                                : 'bg-gray-100 text-gray-600'
                                }`}>
                                {availabilityText}
                            </span>
                        </div>

                        {/* Divider */}
                        <div className="w-full h-px bg-gray-200 my-1"></div>

                        {/* Created at */}
                        <div className="flex items-center gap-1.5 text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <p className="text-xs">Created: {formatDate(listing.created_at)}</p>
                        </div>

                        {/* Rejection reason if applicable */}
                        {listing.status === Status.REJECTED && listing.rejection_reason && (
                            <div className="mt-1 p-2 bg-red-50 rounded-md border border-red-100">
                                <span className="text-xs text-red-600 block">Reason: {listing.rejection_reason}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Link overlay for clickable area */}
                <Link
                    to={`/listing/${listing.id}`}
                    className="absolute inset-0 z-10"
                    aria-label={`View details for ${listing.suburb} property`}
                />
            </div>
        );
    };

    // Skeleton loading card
    const SkeletonCard = () => (
        <div className="w-full flex flex-col rounded-xl bg-white border border-gray-200 h-full shadow-md">
            {/* Image Container */}
            <div className="relative pb-[70%] w-full overflow-hidden rounded-t-xl bg-gray-200 animate-pulse"></div>

            {/* Content area */}
            <div className="p-4 flex flex-col gap-3">
                {/* Price */}
                <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>

                {/* Location */}
                <div className="h-5 bg-gray-200 rounded w-full animate-pulse"></div>

                {/* Availability */}
                <div className="h-6 bg-gray-200 rounded-full w-32 animate-pulse"></div>

                {/* Divider */}
                <div className="w-full h-px bg-gray-200"></div>

                {/* Created at */}
                <div className="h-4 bg-gray-200 rounded w-40 animate-pulse"></div>
            </div>
        </div>
    );

    return (
        <div>
            <MainHeader />
            <PageContainer>
                <div className="px-3">
                    <div className="my-6">
                        <h2 className="text-2xl font-semibold text-gray-800">
                            Manage Listings
                        </h2>
                        <p className="mt-1 text-gray-600">Delete, edit or view the approval status of all your property listings</p>
                    </div>

                    <div className="mb-6">
                        {isLoading ? (
                            // Loading state
                            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {[...Array(3)].map((_, index) => (
                                    <SkeletonCard key={index} />
                                ))}
                            </div>
                        ) : listings.length > 0 ? (
                            // Listings grid
                            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {listings.map((listing) => (
                                    <AdminListingCard key={listing.id} listing={listing} />
                                ))}
                            </div>
                        ) : (
                            // No listings state
                            <div className="text-center py-12 bg-gray-50 rounded-xl">
                                <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mb-4">
                                    <Clock className="h-8 w-8 text-gray-500" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No listings yet</h3>
                                <p className="text-gray-500 mb-6">You haven't created any property listings yet.</p>
                                <Link
                                    to="/create-listing"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2476f2] hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Create Your First Listing
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </PageContainer>

            {/* Delete confirmation modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDelete}
                isDeleting={listingToDelete ? deletingListings.has(listingToDelete.id || '') : false}
                listingSuburb={listingToDelete?.suburb ? capitalizeFirstLetters(listingToDelete.suburb) : undefined}
            />

            <Footer />
        </div>
    );
};

export default ManageListings;